export const siteConfig = {
  siteName: "Drishti Technologies",
  baseUrl: "https://drishtitech.in",
  defaultTitle:
    "Enterprise Applications, Workflow & Mobile Development | Drishti Technologies",
  defaultDescription:
    "Drishti Technologies builds secure enterprise applications, workflow systems, mobile field solutions, ERP integrations and private AI interfaces around real business processes.",
  defaultOgImage: "https://drishtitech.in/assets/projects/fast-accounting.png",
  organizationSchema: {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Drishti Technologies",
    url: "https://drishtitech.in",
    logo: "https://drishtitech.in/favicon.ico",
    sameAs: ["https://fast.drishtitech.in", "https://quizmaster.drishtitech.in"],
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "sales",
        email: "kyi@drishtitech.in",
        telephone: "+91-98-112-55534",
        areaServed: "IN",
        availableLanguage: ["en"],
      },
    ],
  },
};

export const fastProduct = {
  name: "FAST",
  slug: "/products/fast",
  externalUrl: "https://fast.drishtitech.in",
  eyebrow: "Portfolio Product",
  category: "Accounting Workflow Software",
  title: "FAST is document-driven accounting workflow software built for cleaner bookkeeping operations.",
  shortDescription:
    "FAST helps finance teams move from uploaded receipts, invoices and bank statements to reviewed vouchers and core financial reports in one controlled workflow.",
  description:
    "FAST is a live product by Drishti Technologies for businesses that want a calmer, more reviewable accounting workflow. It combines document intake, AI-assisted extraction, voucher drafting, bank statement checks, role-aware workflows and readable reporting so accounting work is easier to process, verify and close.",
  seoTitle:
    "FAST Accounting Workflow Software | Drishti Technologies",
  seoDescription:
    "Explore FAST by Drishti Technologies: document-driven accounting workflow software with document intake, AI-assisted extraction, voucher management, bank statement checks and financial reporting.",
  keywords: [
    "accounting workflow software",
    "bookkeeping software for small business",
    "voucher management system",
    "document based accounting software",
    "bank statement import and reconciliation workflow",
    "financial reporting software",
  ],
  heroStats: [
    {
      label: "Document Intake",
      value: "Receipts, invoices and statements",
    },
    {
      label: "Workflow",
      value: "Review to voucher drafting",
    },
    {
      label: "Controls",
      value: "Duplicate and missing-transaction checks",
    },
    {
      label: "Reports",
      value: "TB, Ledger, P&L and Balance Sheet",
    },
  ],
  features: [
    {
      title: "Document Inbox",
      body:
        "Upload receipts, invoices and statements into one queue for review, follow-up and accounting action.",
    },
    {
      title: "AI-Assisted Extraction",
      body:
        "Run document analysis to extract dates, parties, references, totals and suggested narration from uploaded files.",
    },
    {
      title: "Draft Voucher Creation",
      body:
        "Turn reviewed documents into draft accounting vouchers instead of rebuilding entries manually from scratch.",
    },
    {
      title: "Voucher Workflow",
      body:
        "Work across journal, receipt and payment flows with clearer review before posting to books.",
    },
    {
      title: "Bank Statement Checks",
      body:
        "Import statements, detect already-processed entries, flag missing rows and catch duplicate references in the file.",
    },
    {
      title: "Recurring Warnings",
      body:
        "Spot recurring bank-side patterns that appear to be missing this period and need review.",
    },
    {
      title: "Readable Financial Reporting",
      body:
        "Move from transaction workflow to Trial Balance, General Ledger, Profit & Loss and Balance Sheet in the same system.",
    },
    {
      title: "Role-Aware Multi-Tenant Setup",
      body:
        "Support structured company workflows with permission-aware operations and tenant-specific data boundaries.",
    },
  ],
  workflow: [
    "Upload accounting documents and statements",
    "Analyze and review extracted details",
    "Create or refine draft vouchers",
    "Check imported bank activity and exceptions",
    "Post reviewed entries and generate reports",
  ],
  audiences: [
    "Small and mid-sized businesses that want more control over bookkeeping workflows",
    "Accountants and finance operators handling document-heavy transaction volume",
    "Companies that need cleaner month-end review and traceability",
    "Teams that want business software tailored to real operating flow instead of generic ERP friction",
  ],
  whyItMatters: [
    "Reduces re-entry from paper or PDF source documents",
    "Keeps accounting review tied to original business evidence",
    "Improves traceability between document, voucher and report output",
    "Makes bank-side control checks more explicit and reviewable",
    "Supports a more disciplined close process with less operational noise",
  ],
  capabilities: [
    "Document inbox for receipts, invoices and bank statements",
    "AI-assisted extraction of accounting-relevant fields",
    "Draft voucher creation from reviewed documents",
    "Voucher workflows for payment, receipt and journal entries",
    "Bank statement import and control checks",
    "Trial Balance, Ledger, Profit & Loss and Balance Sheet reporting",
  ],
  portfolioCardDescription:
    "A live document-driven accounting workflow product by Drishti Technologies, built to connect document intake, voucher review, bank checks and financial reporting.",
};

export const fastFaqs = [
  {
    question: "What is FAST?",
    answer:
      "FAST is accounting workflow software by Drishti Technologies. It is designed to help teams move from uploaded accounting documents to reviewed vouchers and financial reports in one controlled process.",
  },
  {
    question: "Who should use FAST?",
    answer:
      "FAST is suited for business owners, accountants, finance operators and companies that want cleaner bookkeeping workflows, stronger reviewability and less manual movement between disconnected tools.",
  },
  {
    question: "Does FAST only store vouchers?",
    answer:
      "No. FAST also covers document intake, AI-assisted extraction, draft voucher creation, bank statement checks and downstream reporting such as Trial Balance, General Ledger, Profit & Loss and Balance Sheet.",
  },
  {
    question: "How does FAST help with bank statement review?",
    answer:
      "FAST can import bank statements, identify already-processed transactions, flag missing rows, detect duplicates inside the statement file and surface recurring warnings for review.",
  },
  {
    question: "Is FAST a live product?",
    answer:
      "Yes. FAST is a live product by Drishti Technologies and is available at fast.drishtitech.in.",
  },
  {
    question: "How can I evaluate FAST for my business?",
    answer:
      "You can review the product page, visit the live FAST site and contact Drishti Technologies for a discussion about fit, workflow requirements and rollout approach.",
  },
];

export const siteFaqs = [
  {
    question: "What does Drishti Technologies build?",
    answer:
      "Drishti Technologies builds AI-enabled software platforms, workflow systems, cybersecurity programs and business applications tailored to real operating needs.",
  },
  {
    question: "Does Drishti Technologies build products as well as services?",
    answer:
      "Yes. Drishti Technologies delivers both client-facing services and product platforms, including FAST for accounting workflows and QuizMaster for certification preparation.",
  },
  {
    question: "What kind of companies work with Drishti Technologies?",
    answer:
      "The company works with enterprises, regulated businesses, media organizations, training-led businesses and teams that need custom workflow or governance-heavy systems.",
  },
  {
    question: "How do I learn more about FAST?",
    answer:
      "You can visit the dedicated FAST page on Drishti Technologies, then continue to the live product at fast.drishtitech.in for more context.",
  },
];
